import React, { useEffect, useRef, useState } from 'react';
import { Moment } from 'moment';
import {
  Alert,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Modal,
  Row,
  Typography,
} from 'antd';
// Types
import { TicketEventDetailsFields } from 'api/ticketing/types/TicketEventDetailsFields';
import { TicketEventStatus } from 'api/graphql-global-types';
// Hooks
import { useTicketEventChanges } from 'pages/Events/hooks/useTicketEventChanges';
// Helpers
import {
  fromVenueInstant,
  futureInVenueRule,
  getTimezoneLabel,
  validateForm,
  venueDateTimePickerProps,
} from 'pages/Events/helpers';
import { getShiftedSchedule } from 'pages/Events/components/TicketEventForm/helpers';
import { runTicketingChange } from 'pages/Events/runTicketingChange';
import {
  buildRescheduleEventInput,
  getRescheduleFormValues,
  RescheduleFormValues,
} from './helpers';
// Constants
import { shortDateTimeFormat } from 'constants/global';
// Styles
import styles from './RescheduleModal.module.scss';

const { Paragraph, Text } = Typography;

type EventDetails = TicketEventDetailsFields;

type RescheduleModalProps = {
  event: EventDetails;
  visible: boolean;
  onClose: () => void;
  onRescheduled: () => void;
};

const RescheduleModal = ({
  event,
  visible,
  onClose,
  onRescheduled,
}: RescheduleModalProps): JSX.Element => {
  const [form] = Form.useForm<RescheduleFormValues>();
  const [moveTogether, setMoveTogether] = useState(true);
  const previousStart = useRef<Moment | null>(null);
  const { reschedule, busy } = useTicketEventChanges();

  useEffect(() => {
    if (visible) {
      const values = getRescheduleFormValues(event);
      form.setFieldsValue(values);
      previousStart.current = values.startsAt;
    }
  }, [visible, event, form]);

  // Moving the start drags the other times along by the same amount
  const handleValuesChange = (changed: Partial<RescheduleFormValues>): void => {
    if (!changed.startsAt) {
      return;
    }

    if (moveTogether && previousStart.current) {
      form.setFieldsValue(
        getShiftedSchedule(
          form.getFieldsValue(),
          changed.startsAt.diff(previousStart.current)
        )
      );
    }

    previousStart.current = changed.startsAt;
  };

  const handleOk = async (): Promise<void> => {
    const values = await validateForm(form);

    if (!values) {
      return;
    }

    const rescheduled = await runTicketingChange(
      () => reschedule(buildRescheduleEventInput(event, values)),
      event.publishedAt
        ? 'The event is rescheduled. Sales are paused: resume them when ready.'
        : 'The event is rescheduled',
      'Failed to reschedule the event'
    );

    if (rescheduled) {
      onRescheduled();
    }
  };

  const picker = (
    <DatePicker
      {...venueDateTimePickerProps(event.timezone)}
      className={styles.picker}
    />
  );

  const after =
    (field: keyof RescheduleFormValues, label: string) =>
    ({ getFieldValue }: { getFieldValue: (name: string) => Moment }) => ({
      validator: (_: unknown, value?: Moment | null) => {
        const other = getFieldValue(field);

        return !value || !other || !value.isBefore(other)
          ? Promise.resolve()
          : Promise.reject(new Error(`Must not be before ${label}`));
      },
    });

  return (
    <Modal
      title="Reschedule event"
      visible={visible}
      onCancel={onClose}
      onOk={handleOk}
      okText="Reschedule"
      confirmLoading={busy}
      destroyOnClose
      width={720}
    >
      <Paragraph type="secondary">
        Times are in {getTimezoneLabel(event.timezone, event.startsAt)}. Ticket
        holders are told about the new date and their tickets stay valid.
      </Paragraph>
      {event.publishedAt && event.status !== TicketEventStatus.Draft && (
        <Alert
          className={styles.alert}
          type="info"
          showIcon
          message="A published event comes back with sales paused. Resume them from the event page when you're ready."
        />
      )}
      <Checkbox
        className={styles.moveTogether}
        checked={moveTogether}
        onChange={(changeEvent) => setMoveTogether(changeEvent.target.checked)}
      >
        Move doors, end, admission and sales close with the start
      </Checkbox>
      <Form<RescheduleFormValues>
        form={form}
        layout="vertical"
        onValuesChange={handleValuesChange}
      >
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="startsAt"
              label="Starts"
              rules={[
                { required: true, message: 'Set the start time' },
                futureInVenueRule(
                  event.timezone,
                  'The new start must be in the future'
                ),
              ]}
            >
              {picker}
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="endsAt"
              label="Ends"
              dependencies={['startsAt']}
              rules={[
                { required: true, message: 'Set the end time' },
                after('startsAt', 'the start'),
              ]}
            >
              {picker}
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="doorsOpenAt"
              label="Doors open"
              rules={[{ required: true, message: 'Set when doors open' }]}
            >
              {picker}
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="admissionClosesAt"
              label="Admission closes"
              dependencies={['doorsOpenAt']}
              rules={[
                { required: true, message: 'Set when admission closes' },
                after('doorsOpenAt', 'doors open'),
                futureInVenueRule(event.timezone, 'Must be in the future'),
              ]}
            >
              {picker}
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="salesOpenAt" label="Sales open">
              {picker}
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="salesCloseAt"
              label="Sales close"
              dependencies={['salesOpenAt']}
              rules={[
                { required: true, message: 'Set when sales close' },
                after('salesOpenAt', 'sales open'),
              ]}
            >
              {picker}
            </Form.Item>
          </Col>
        </Row>
      </Form>
      {event.originalStartsAt && (
        <Text type="secondary">
          Originally scheduled for{' '}
          {fromVenueInstant(event.originalStartsAt, event.timezone).format(
            shortDateTimeFormat
          )}
          .
        </Text>
      )}
    </Modal>
  );
};

export default RescheduleModal;
